import type { GridColDef } from '@mui/x-data-grid';
import { DEFAULT_CURRENCY, type RecurringRevenueReport } from '@vy/types';
import { sumBy, type ItemOf } from '@vy/utils';
import { useCallback, useMemo, useState } from 'react';
import type { GridApi } from '@mui/x-data-grid-premium';
import { VyTableWaterfallPremium } from '../../../../../components/new-design/table/vy-table/VyWaterfallTablePremium';
import { applyRecurringRevenueSearch } from '../../utils/apply-recurring-revenue-search';
import {
  buildContractsByCustomer,
  buildCustomerMrrByMonth,
  classifyMonthlyMovements,
  prepareRows,
} from '../../utils/chart-movements';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import {
  extractMonthlyColumnKeys,
  TOTALS_CHILD_ROW_ID_PREFIX,
  OPTIONAL_BASE_COLUMN_FIELDS,
  BASE_COLUMN_FIELDS,
  MONTH_COLUMN_FIELD_PREFIX,
} from './RecurringRevenueReportTableColumns';
import {
  expandRecurringRevenueRows,
  type ExpandedRecurringRevenueRow,
} from './recurring-revenue-report-row-expander';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];
const DEFAULT_PAGE_SIZE = 100;

const MOVEMENT_TYPES = [
  { key: 'new', label: 'New' },
  { key: 'expansion', label: 'Expansion' },
  { key: 'contraction', label: 'Contraction' },
  { key: 'churn', label: 'Churn' },
  { key: 'reactivation', label: 'Reactivation' },
] as const;

type RecurringRevenueReportTableProps = {
  // Worker outputs: filter-applied flat report and its precomputed expansion.
  // When `searchText` is empty we render `expandedRows` directly; otherwise we
  // search-filter the flat report and re-expand only the matching subset.
  filteredReport: RecurringRevenueReport;
  expandedRows: ExpandedRecurringRevenueRow[];
  columns: GridColDef<ExpandedRecurringRevenueRow>[];
  searchText?: string;
  apiRef?: React.MutableRefObject<GridApi>;
  isLoading?: boolean;
};

export const RecurringRevenueReportTable: React.FC<RecurringRevenueReportTableProps> = ({
  filteredReport,
  expandedRows,
  columns,
  searchText,
  isLoading,
  apiRef,
}) => {
  const searchFilteredFlat = useMemo(
    () => applyRecurringRevenueSearch(filteredReport, searchText),
    [filteredReport, searchText],
  );

  const visibleExpandedRows = useMemo(() => {
    if (searchFilteredFlat === filteredReport) return expandedRows;
    return expandRecurringRevenueRows(searchFilteredFlat);
  }, [searchFilteredFlat, filteredReport, expandedRows]);

  const monthKeys = useMemo(() => extractMonthlyColumnKeys(searchFilteredFlat), [searchFilteredFlat]);

  const totalsRow = useMemo((): ItemOf<RecurringRevenueReport> => {
    const currentMrrUsd = sumBy(searchFilteredFlat, (row) => row.currentMrrUsd);
    const totalArrUsd = sumBy(searchFilteredFlat, (row) => row.totalArrUsd);

    const monthlyTotals = monthKeys.reduce<Record<string, number>>((totals, key) => {
      totals[key] = sumBy(searchFilteredFlat, (row) => {
        const value = (row as Record<string, unknown>)[key];

        if (typeof value === 'number') {
          return value;
        }

        if (typeof value === 'object' && value !== null && 'totalMrr' in value) {
          return (value as { totalMrr: number }).totalMrr;
        }

        return 0;
      });
      return totals;
    }, {});

    return {
      contractId: TOTALS_ROW_ID,
      customerName: 'Total',
      subsidiaryName: null,
      parentName: null,
      contractName: '',
      contractStartDate: '1970-01-01',
      contractEndDate: null,
      contractStatus: 'Active',
      originalCurrency: DEFAULT_CURRENCY,
      currentMrrInOriginalCurrency: 0,
      exchangeRate: null,
      currentMrrUsd,
      totalArrUsd,
      ...monthlyTotals,
    } as ItemOf<RecurringRevenueReport>;
  }, [searchFilteredFlat, monthKeys]);

  const [isTotalsExpanded, setIsTotalsExpanded] = useState(false);
  const toggleTotalsExpanded = useCallback(() => setIsTotalsExpanded((prev) => !prev), []);

  // Classify movements per row × month from MRR changes (matching the chart's
  // logic), then sum by movement type. We can't rely on the backend's
  // `breakdown.movement` field because it's null on this branch's data.
  const monthlyMovementsByKey = useMemo(() => {
    const preparedRows = prepareRows(searchFilteredFlat, monthKeys);
    const contracts = buildContractsByCustomer(preparedRows);
    const customerMrr = buildCustomerMrrByMonth(preparedRows, monthKeys);
    return classifyMonthlyMovements(customerMrr, contracts, monthKeys);
  }, [searchFilteredFlat, monthKeys]);

  const totalsChildRows = useMemo((): ItemOf<RecurringRevenueReport>[] => {
    return MOVEMENT_TYPES.map(({ key, label }, index) => {
      const monthlyValues = monthKeys.reduce<Record<string, number>>((values, monthKey) => {
        const point = monthlyMovementsByKey.get(monthKey);
        if (!point) {
          values[monthKey] = 0;
          return values;
        }

        switch (key) {
          case 'new':
            values[monthKey] = point.new;
            break;
          case 'expansion':
            values[monthKey] = point.expansion;
            break;
          case 'reactivation':
            values[monthKey] = point.reactivation;
            break;
          case 'contraction':
            values[monthKey] = point.contraction;
            break;
          case 'churn':
            values[monthKey] = point.churn;
            break;
          default:
            values[monthKey] = 0;
        }
        return values;
      }, {});

      return {
        contractId: `${TOTALS_CHILD_ROW_ID_PREFIX}${index}`,
        customerName: label,
        subsidiaryName: null,
        parentName: null,
        contractName: '',
        contractStartDate: '1970-01-01',
        contractEndDate: null,
        contractStatus: 'Active',
        originalCurrency: DEFAULT_CURRENCY,
        currentMrrInOriginalCurrency: 0,
        exchangeRate: null,
        currentMrrUsd: 0,
        totalArrUsd: 0,
        ...monthlyValues,
      } as ItemOf<RecurringRevenueReport>;
    });
  }, [monthKeys, monthlyMovementsByKey]);

  const expandableTotalsRow = useMemo(
    () =>
      (({
        ...totalsRow,
        __isExpanded: isTotalsExpanded,
        __toggleExpand: toggleTotalsExpanded
      }) as ItemOf<RecurringRevenueReport>),
    [totalsRow, isTotalsExpanded, toggleTotalsExpanded],
  );

  const pinnedRows = useMemo(() => {
    const bottomRows = isTotalsExpanded ? [expandableTotalsRow, ...totalsChildRows] : [expandableTotalsRow];

    return { bottom: bottomRows as unknown as ExpandedRecurringRevenueRow[] };
  }, [expandableTotalsRow, isTotalsExpanded, totalsChildRows]);

  const getRowId = useCallback((row: ExpandedRecurringRevenueRow) => row.id ?? row.contractId, []);

  const pinnedColumns = useMemo(() => ({ left: ['customerName'] }), []);

  const initialColumnVisibility = useMemo(() => {
    const customFieldFields = columns
      .map((column) => column.field)
      .filter(
        (field) =>
          !BASE_COLUMN_FIELDS.has(field) && !field.startsWith(MONTH_COLUMN_FIELD_PREFIX),
      );

    return Object.fromEntries(
      [...OPTIONAL_BASE_COLUMN_FIELDS, ...customFieldFields].map((field) => [field, false]),
    );
  }, [columns]);

  const getRowClassName = useCallback(
    (params: { row: ExpandedRecurringRevenueRow }) =>
      params.row.contractId.startsWith(TOTALS_CHILD_ROW_ID_PREFIX) ? 'vy-child-row' : '',
    [],
  );

  return (
    <VyTableWaterfallPremium
      apiRef={apiRef}
      columns={columns}
      items={visibleExpandedRows}
      loadingItems={isLoading}
      getRowId={getRowId}
      getRowClassName={getRowClassName}
      pinnedRows={pinnedRows}
      pinnedColumns={pinnedColumns}
      density="compact"
      initialColumnVisibility={initialColumnVisibility}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      initialPageSize={DEFAULT_PAGE_SIZE}
    />
  );
};
